/// <amd-module name="Build/SourceProviders/Base" />
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import {
    IBranchInfo,
    ICommitInfo,
    IContentInfo,
    IContentLinkData,
    ILinkData,
    ILinkInfo,
    IProjectInfo,
    IPullrequestData,
    IPullrequestInfo,
    IRepositoryInfo,
    ISourceProvider,
    ICommitDiffInfo
} from "Build/SourceProviders/Common";

import { IVCNavigationService } from "VC/Common/Navigation";
import { TfvcShelvesetRef } from "VC/Common/Contracts/Git";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export abstract class Base implements ISourceProvider {
    protected pageContext: IVssPageContext;
    protected emptyNullPromise: Promise<null>;
    private _vcContributionPromise: Promise<void> | undefined;
    private _vcNavigationService: Promise<IVCNavigationService> | undefined;
    private _buildClient: IBuildRestClient | undefined;

    public abstract getKey(): string;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.emptyNullPromise = Promise.resolve(null);
    }

    public getName(): string {
        return "";
    }

    public getRepoIconName(): string {
        return "";
    }

    public getNormalizedBranch(branch: string, projectInfo: IProjectInfo) {
        return branch;
    }

    public getNormalizedCommit(commit: string) {
        return commit;
    }

    public getCommitIconName(): string {
        return "BranchCommit";
    }

    public getProjectName(repositoryId: string): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public fetchBranchLink(info: IBranchInfo, projectName?: string): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public fetchContentLinks(info: IContentInfo[]): Promise<IContentLinkData[] | null> {
        return this.emptyNullPromise;
    }

    public fetchContentLink(info: IContentInfo): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public fetchRepositoryLink(info: IRepositoryInfo, projectName?: string): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public fetchPRCommitLink(info: IBranchInfo, projectName?: string): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public fetchCommitDiffLink(info: ICommitDiffInfo, projectName?: string): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public fetchLinks(info: ILinkInfo[]): Promise<ILinkData[] | null> {
        return this.vcNavigationService.then((service) => {
            let linkData: ILinkData[] = [];
            (info || []).forEach((linkInfo) => {
                linkData.push({
                    originalId: linkInfo.commitId,
                    commitId: this.getCommitText(linkInfo.commitId),
                    branchLink: this._getBranchLink({
                        branchName: linkInfo.branchName,
                        links: linkInfo.links,
                        repositoryName: linkInfo.repositoryName,
                        repositoryUrl: linkInfo.repositoryUrl
                    }, service, linkInfo.projectName),
                    commitLink: this._getCommitLink({
                        commitId: linkInfo.commitId,
                        displayUri: linkInfo.displayUri,
                        repositoryName: linkInfo.repositoryName
                    }, service, linkInfo.projectName),
                    repositoryLink: this._getRepositoryLink({
                        repositoryName: linkInfo.repositoryName,
                        repositoryUrl: linkInfo.repositoryUrl
                    }, service, linkInfo.projectName)
                });
            });
            return linkData;
        });
    }

    public fetchPullrequestData(info: IPullrequestInfo): Promise<IPullrequestData | null> {
        return this.emptyNullPromise;
    }

    public getBranchText(branch: string): string {
        return branch;
    }

    public getBranchIconName(branch: string): string {
        return "OpenSource";
    }

    public fetchCommitLink(info: ICommitInfo, projectName?: string): Promise<string | null> {
        return this.emptyNullPromise;
    }

    public getCommitText(commit: string): string {
        return commit || "";
    }

    public getMaxWorkItems(): number | undefined {
        return undefined;
    }

    public fetchShelvesets(owner: string): Promise<TfvcShelvesetRef[]> {
        return Promise.resolve([]);
    }

    protected get vcNavigationService(): Promise<IVCNavigationService> {
        if (!this._vcNavigationService) {
            this._vcNavigationService = this.getVcCode().then(() => {
                return this.pageContext.getService<IVCNavigationService>("IVCNavigationService");
            });
        }

        return this._vcNavigationService;
    }

    protected getVcCode(): Promise<void> {
        if (!this._vcContributionPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this._vcContributionPromise = contributionService.getContributionAsync("ms.vss-code-web.common-content");
        }

        return this._vcContributionPromise;
    }

    protected _getBuildClient(): IBuildRestClient {
        if (!this._buildClient) {
            this._buildClient = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        }

        return this._buildClient;
    }

    protected _getBranchLink(info: IBranchInfo, service?: IVCNavigationService, projectName?: string): string {
        return "";
    }

    protected _getCommitLink(info: ICommitInfo, service?: IVCNavigationService, projectName?: string): string {
        return "";
    }

    protected _getRepositoryLink(info: IRepositoryInfo, service?: IVCNavigationService, projectName?: string): string {
        return "";
    }
}
